export const HELP_QUERY_PARAM = "help";

export type HelpGuideId =
  | "overview"
  | "belege"
  | "credits"
  | "price-anomaly-review"
  | "order-confirmations"
  | "price-analysis"
  | "article"
  | "offers"
  | "cost-centers"
  | "imports-sources"
  | "members";

export type HelpGuideAudience = "all" | "admin";

export type HelpGuideGroupId =
  | "general"
  | "accounting"
  | "purchasing"
  | "controlling"
  | "price-analysis"
  | "data-administration";

export type HelpUseCaseId =
  | "UC-OV-001"
  | "UC-BE-001"
  | "UC-BE-002"
  | "UC-BE-003"
  | "UC-BE-004"
  | "UC-BE-005"
  | "UC-BE-006"
  | "UC-EK-002"
  | "UC-EK-003"
  | "UC-EK-004"
  | "UC-PA-001"
  | "UC-PA-002"
  | "UC-CC-001"
  | "UC-CC-002"
  | "UC-CC-005"
  | "UC-IM-001"
  | "UC-IM-002"
  | "UC-DB-001"
  | "UC-AD-001";

export type HelpRoutePath =
  | "/"
  | "/belege"
  | "/auftragsbestaetigungen"
  | "/reports/variance"
  | "/articles"
  | "/preisanfrage"
  | "/angebote"
  | "/orders"
  | "/rechnungspruefung"
  | "/preisspiegel"
  | "/bestellungen"
  | "/warenkorb"
  | "/einstellungen"
  | "/cost-centers"
  | "/projects"
  | "/imports"
  | "/imports/review"
  | "/coverage"
  | "/members";

export type HelpInternalHref =
  | HelpRoutePath
  | `${HelpRoutePath}?${string}`;

export type HelpGuideStep = {
  title: string;
  text: string;
};

export type HelpGuideTerm = {
  term: string;
  explanation: string;
};

export type HelpGuideLink = {
  label: string;
  to: HelpInternalHref;
};

export type HelpGuide = {
  id: HelpGuideId;
  title: string;
  listLabel: string;
  audience: HelpGuideAudience;
  useCaseIds: readonly HelpUseCaseId[];
  purpose: string;
  steps: readonly HelpGuideStep[];
  terms: readonly HelpGuideTerm[];
  saving: string;
  boundary: string;
  links: readonly HelpGuideLink[];
};

export type HelpGuideGroupDefinition = {
  id: HelpGuideGroupId;
  label: string;
  guideIds: readonly HelpGuideId[];
};

export type AvailableHelpGuideGroup = {
  id: HelpGuideGroupId;
  label: string;
  guides: readonly HelpGuide[];
};

export type HelpRouteMapping = {
  kind: "exact" | "children";
  path: HelpRoutePath;
  guideId: HelpGuideId;
};

export const HELP_GUIDE_GROUPS: readonly HelpGuideGroupDefinition[] = [
  // Gruppen folgen dem Menü seit Beta 30 (#630).
  { id: "general", label: "Allgemein", guideIds: ["overview", "members"] },
  { id: "purchasing", label: "Einkauf", guideIds: ["article", "offers", "order-confirmations"] },
  { id: "controlling", label: "Prüfung & Kostenstellen", guideIds: ["belege", "price-anomaly-review", "credits", "cost-centers"] },
  { id: "price-analysis", label: "Analyse", guideIds: ["price-analysis"] },
  { id: "data-administration", label: "Belege hochladen", guideIds: ["imports-sources"] },
] as const;

export const HELP_GUIDES: readonly HelpGuide[] = [
  {
    id: "overview",
    title: "Die Übersicht nutzen",
    listLabel: "Übersicht",
    audience: "all",
    useCaseIds: ["UC-OV-001"],
    purpose:
      "Die Übersicht zeigt, was gerade auf dich wartet, und führt direkt zu deinen eigenen Vorgängen.",
    steps: [
      {
        title: "Artikel oder Beleg finden",
        text: "Die Schnellsuche auf der Übersicht zeigt zuerst passende Artikel, danach Rechnungen und Gutschriften. Artikel werden nach Nummer und Beschreibung gesucht, Belege nach Belegnummer, Großhändler und Besteller. Mit den Pfeiltasten wählst du einen Treffer und mit Enter öffnest du ihn. Ohne Treffer bleibst du auf der Übersicht und kannst den Suchbegriff ändern.",
      },
      {
        title: "Offene Schritte erledigen",
        text: "„Zu erledigen“ zeigt nur, was jetzt offen ist und was du erledigen darfst: Preisabweichungen prüfen oder unsicher erkannte Importe entscheiden. Ein Klick führt in die passende, gefilterte Liste. Ist nichts offen, steht dort „Alles erledigt“.",
      },
      {
        title: "An eigenen Vorgängen weiterarbeiten",
        text: "Darunter stehen deine offenen Warenkörbe, deine letzten Uploads mit ihrem Stand und die Artikel und Belege, die du zuletzt geöffnet hast. Neue Rechnungen, Angebote oder Auftragsbestätigungen ziehst du direkt in die Karte „Belege hochladen“. Die teuersten Preisabweichungen und das Einkaufsvolumen findest du unter „Analyse“.",
      },
      {
        title: "Menü verstehen",
        text: "Oben rechts erreichst du Konto, Bestellungen und Warenkorb. Auf dem Smartphone öffnet das Paket-Symbol die Bestellungen. Darunter liegt das Hauptmenü: Startseite, Artikel, Belege, Rechnungsprüfung, Kostenstellen, Analyse und Preisspiegel; ab Tablet-Breite bleibt es beim Scrollen oben stehen. Du siehst nur, wofür du berechtigt bist. Hilfe, Neuerungen und Einstellungen findest du im Konto-Menü.",
      },
    ],
    terms: [
      {
        term: "Zu erledigen",
        explanation: "Offene Schritte, für die du berechtigt bist, mit ihrer aktuellen Anzahl.",
      },
      {
        term: "Unsicher erkannter Import",
        explanation:
          "Ein hochgeladener Beleg, dessen erkannte Daten das Controlling übernehmen, korrigieren oder verwerfen muss.",
      },
      {
        term: "Zuletzt angesehen",
        explanation: "Die zuletzt geöffneten Artikel und Belege, nur in diesem Browser für dich gemerkt.",
      },
    ],
    saving:
      "Auf der Übersicht selbst wird keine Prüfentscheidung gespeichert.",
    boundary:
      "Die Anzahlen der Übersicht sind ein aktueller Bestand offener Arbeit, keine Produktivitäts-, Erfolgs- oder Einsparquote. Die vollständige Aufschlüsselung der Prüfzustände steht auf der Belegliste.",
    links: [
      { label: "Offene Preisprüfungen öffnen", to: "/rechnungspruefung?review=open" },
      { label: "Analyse öffnen", to: "/reports/variance" },
    ],
  },
  {
    id: "belege",
    title: "Belege und Rechnungsprüfung",
    listLabel: "Belege",
    audience: "all",
    useCaseIds: ["UC-BE-001", "UC-BE-003"],
    purpose:
      "„Belege“ ist das Archiv aller eingelesenen Rechnungen, Gutschriften, Angebote und Auftragsbestätigungen. „Rechnungsprüfung“ ist die Arbeitsliste: Rechnungen und Gutschriften mit offenen Preisabweichungen.",
    steps: [
      {
        title: "Beleg nachschlagen",
        text: "Öffne „Belege“ im Hauptmenü. Wähle oben die Belegart und grenze mit Zeitraum, Suche, Kostenstelle und Großhändler ein. Gesucht wird nach Belegnummer, ER-Nummer, Großhändler und Besteller. Die PDF-Ansicht öffnest du direkt an der Belegnummer. „Filter zurücksetzen“ zeigt wieder alle Belege, neueste zuerst.",
      },
      {
        title: "Offene Rechnungen prüfen",
        text: "Öffne „Rechnungsprüfung“. Die Zahl im Menü nennt die Rechnungen, die noch zu prüfen sind. Die Liste öffnet immer unter „Zu prüfen“; „In Klärung“, „Erledigt“ und „Alle“ zeigen den weiteren Stand. „Offene Mehrkosten“ sortiert nach der finanziellen Auswirkung. Mit „Mir zuweisen“ übernimmst du eine Rechnung, „Meine“ zeigt nur deine.",
      },
      {
        title: "Im Beleg entscheiden",
        text: "Ein Klick auf eine Rechnung öffnet sie mit Original und Positionen. Dort entscheidest du je auffälliger Position. Die Pfeile im Beleg führen durch dieselbe Auswahl weiter.",
      },
    ],
    terms: [
      {
        term: "Offene Kostenstellen-Zuordnung",
        explanation: "Für eine Gutschriftposition fehlt ein eindeutiger Kostenstellenbeleg. Eine berechtigte Person kann Original und Rechnung vergleichen und ihre Entscheidung begründet speichern.",
      },
      {
        term: "Prüfung erforderlich",
        explanation: "Für mindestens eine auffällige Position fehlt noch eine Entscheidung.",
      },
      {
        term: "Klärung angefragt",
        explanation: "Eine Rückfrage läuft, aber eine Gutschrift ist noch nicht zugesagt.",
      },
      {
        term: "Gutschrift erwartet",
        explanation: "Eine Gutschrift wurde angefordert oder zugesagt.",
      },
      {
        term: "Ausgebucht",
        explanation: "Die Abweichung wurde akzeptiert und abgeschlossen.",
      },
      {
        term: "Offene Mehrkosten",
        explanation:
          "Summe positiver Mehrkosten aus Rechnungspositionen, deren Prüfstatus noch offen ist. Laufende Klärungen, erledigte Positionen und Gutschriften zählen nicht.",
      },
      {
        term: "Gutschrift erhalten",
        explanation:
          "Dokumentierter Abschlussstatus einer Preisprüfung. Er kann manuell gesetzt sein und ist daher nicht automatisch eine bestätigte Beleg-Zuordnung.",
      },
      {
        term: "Nicht näher zugeordnet",
        explanation:
          "Für eine ältere oder nicht eindeutig zuordenbare Position ist keine fachliche Aktion dokumentiert. Prüfen Sie sie separat.",
      },
    ],
    saving:
      "Die Listen speichern keine Prüfentscheidung. Entschieden wird im einzelnen Beleg.",
    boundary:
      "Mehrkosten beruhen auf belegten früheren Rechnungspreisen. Ob Konditionen vergleichbar sind, prüfst du am Original.",
    links: [
      { label: "Rechnungsprüfung öffnen", to: "/rechnungspruefung" },
      { label: "Belege öffnen", to: "/belege" },
    ],
  },
  {
    id: "credits",
    title: "Gutschriften verfolgen",
    listLabel: "Gutschriften",
    audience: "all",
    useCaseIds: ["UC-BE-006"],
    purpose:
      "Hier sehen Sie erwartete Gutschriften, eingelangte Gutschrift-Belege und bestätigte Zuordnungen getrennt voneinander.",
    steps: [
      {
        title: "Gutschriften verfolgen",
        text: "Prüfen Sie den aktuellen Erwartungsbestand und vergleichen Sie ihn mit eingelangten Gutschrift-Belegen und bestätigten Zuordnungen.",
      },
      {
        title: "Eingelangte Belege einordnen",
        text: "Wählen Sie einen Monat, ein Jahr oder einen eigenen Zeitraum. Beim eigenen Zeitraum lädt eine gültige Änderung am Von- oder Bis-Datum automatisch. Beide Daten zählen mit; die Auswahl richtet sich nach dem Belegdatum der Gutschrift und verändert nicht den aktuellen Erwartungsbestand. „Belege im Zeitraum öffnen“ führt zu den zugehörigen Gutschriften in der Belegliste. Die Zeitraumwahl bleibt in der Adresse und beim Zurückkehren aus einem Beleg erhalten. Sie wird persönlich und getrennt von der Belegliste gemerkt. Ein Monat und ein Jahr wandern beim nächsten Besuch mit; eigene Daten bleiben fest. „Filter zurücksetzen“ wählt wieder einen Monat. Links mit eigener Auswahl verändern deine persönliche Ansicht erst, wenn du selbst die Auswahl änderst.",
      },
      {
        title: "Zuordnungen nachvollziehen",
        text: "Öffnen Sie einen Vorschlag im Belegdetail und prüfen Sie dort Rechnung, Position, Betrag und PDF-Nachweis.",
      },
    ],
    terms: [
      {
        term: "Erwarteter Differenzbetrag",
        explanation:
          "Aus der geprüften Preisabweichung abgeleiteter Betrag. Er ist noch kein eingelangter Gutschrift-Beleg.",
      },
      {
        term: "Offener Differenzbetrag",
        explanation:
          "Teil des erwarteten Differenzbetrags, der noch nicht durch eine bestätigte Gutschrift-Zuordnung gedeckt ist. Er ist kein zugesagter Lieferantenbetrag.",
      },
      {
        term: "Gutschrift-Belege im Zeitraum",
        explanation:
          "Gutschriften mit Belegdatum innerhalb des exakt angezeigten Zeitraums. Bei einem eigenen Zeitraum zählen das Von- und das Bis-Datum mit.",
      },
      {
        term: "Bestätigt zugeordnet",
        explanation:
          "Beträge aus nachvollziehbar bestätigten Verknüpfungen zwischen einem Gutschrift-Beleg und der ursprünglichen Preisprüfung.",
      },
      {
        term: "Zuordnung nicht aktiviert",
        explanation:
          "Erwartungen und Gutschrift-Belege sind sichtbar, automatische Vorschläge und Deckungswerte aber noch nicht verfügbar.",
      },
      {
        term: "Datenprüfung erforderlich",
        explanation:
          "Ein Belegdatum oder eine bestätigte Zuordnung ist widersprüchlich. TruuCost zeigt die betroffenen Zuordnungswerte bis zur Klärung nicht als verlässlich an.",
      },
    ],
    saving:
      "Auf dieser Seite wird keine Zuordnung gespeichert. Eine Bestätigung oder Aufhebung erfolgt im jeweiligen Belegdetail.",
    boundary:
      "Währungen werden getrennt ausgewiesen. Ein manuell gesetztes „Gutschrift erhalten“ ist noch keine bestätigte Beleg-Zuordnung.",
    links: [
      {
        label: "Erwartete Gutschriften öffnen",
        to: "/rechnungspruefung?review=credit_expected",
      },
      { label: "Gutschrift-Belege öffnen", to: "/rechnungspruefung?type=gutschrift&review=all" },
      { label: "Alle Belege öffnen", to: "/belege" },
    ],
  },
  {
    id: "price-anomaly-review",
    title: "Preisauffälligkeit prüfen",
    listLabel: "Preisauffälligkeit prüfen",
    audience: "all",
    useCaseIds: ["UC-BE-002", "UC-BE-004", "UC-BE-005"],
    purpose:
      "Hier prüfen Sie eine auffällige Rechnungsposition und halten Ihre Entscheidung nachvollziehbar fest.",
    steps: [
      {
        title: "Nachweise prüfen",
        text: "Vergleichen Sie Netto-Preis, die für Ihren Betrieb aktive Vergleichsregel, Mehrkosten, PDF und den bisherigen Preisverlauf. Die Artikelhistorie zeigt die neuesten Rechnungspositionen zuerst und markiert die aktuell geprüfte Position. Sie lässt sich auch nach zugeordneten ER-Nummern durchsuchen; Nummern und Nachweise stehen direkt beim Beleg.",
      },
      {
        title: "Entscheidung wählen",
        text: "Wählen Sie Klärung anfragen, Gutschrift erwartet oder Ausbuchen und ergänzen Sie eine kurze Begründung.",
      },
      {
        title: "E-Mail selbst versenden",
        text: "Kopieren Sie bei Bedarf den vorbereiteten Text in Ihr eigenes E-Mail-Programm und senden Sie ihn von dort.",
      },
      {
        title: "Prüfentscheidung speichern",
        text: "Speichern Sie anschließend die gewählte Entscheidung und den Kommentar in TruuCost.",
      },
      {
        title: "Gutschrift später zuordnen",
        text: "Eine eingegangene Gutschrift bleibt ein eigener Beleg und kann der erwarteten Gutschrift zugeordnet werden.",
      },
    ],
    terms: [
      {
        term: "Aktive Preisprüfungsregel",
        explanation:
          "Sie legt Vergleichszeitraum, prozentuale Mindestabweichung und optional Mindestmehrkosten fest. Eine gespeicherte Änderung gilt nach der nächsten regulären Datenaktualisierung.",
      },
      {
        term: "Klärung angefragt",
        explanation: "Eine Rückfrage läuft; es gibt noch keine zugesagte Gutschrift.",
      },
      {
        term: "Gutschrift erwartet",
        explanation: "Eine Gutschrift wurde angefordert oder zugesagt.",
      },
      {
        term: "Ausgebucht",
        explanation: "Sie akzeptieren die Differenz und schließen die Prüfung ab.",
      },
      {
        term: "Mehrkosten",
        explanation:
          "Preisunterschied über der angezeigten Vergleichsbasis für genau diese Position.",
      },
    ],
    saving:
      "Prüfentscheidung speichern dokumentiert Status, Kommentar, Benutzer, Zeitpunkt und die dabei sichtbare Vergleichsbasis.",
    boundary:
      "TruuCost versendet keine E-Mail. Kopieren Sie den Text in Ihr Mailprogramm. Das Speichern dokumentiert nur Ihre Prüfentscheidung.",
    links: [
      { label: "Zur Belegübersicht", to: "/belege" },
      { label: "Analyse öffnen", to: "/reports/variance" },
    ],
  },
  {
    id: "order-confirmations",
    title: "Auftragsbestätigungen prüfen",
    listLabel: "Auftragsbestätigungen",
    audience: "all",
    useCaseIds: ["UC-EK-004"],
    purpose:
      "Hier prüfen Sie bestätigte Einkaufspreise vor der weiteren Verarbeitung gegen frühere Rechnungen und halten Ihre interne Entscheidung fest.",
    steps: [
      {
        title: "Offene Prüfungen finden",
        text: "Unter Bestellungen › Auftragsbestätigungen zeigen die runden Status-Filter, wie viele Belege Prüfung, Rückfrage oder Entscheidung brauchen. Suchen Sie dort nach Belegnummer oder Großhändler. Auftragsbestätigungen finden Sie auch unter „Belege“ und können die Belegart dort eingrenzen. Auch wenn ein Beleg nicht geladen werden kann, führt „Zur Prüfliste“ zur vorherigen Liste mit derselben Suche und denselben Filtern zurück; im Detail führt „Nächster offener“ durch die gefilterte Liste zu weiteren prüfbaren Belegen.",
      },
      {
        title: "Positionen und Nachweise prüfen",
        text: "Die vollständigen Positionen stehen vor der Entscheidung. Vergleichen Sie AB-Preis, historische Referenz und Warnungen. Der Bestellpreis ist derzeit unbekannt; „–“ ist kein Preis von 0 €. „Grundlage ansehen“ zeigt Zeitraum, Rechnungspositionen und Bestpreis. Das Augen-Symbol am Artikel öffnet die Position im Original; das vollständige PDF steht oben am Beleg. Mehrkosten sind rot, Ersparnisse grün und Nullwerte neutral. Auf dem Handy stehen dieselben Angaben untereinander. „Zuordnungen klären“ führt zu unklaren Artikelangaben dieses Dokuments. Nach der Entscheidung gelangen Sie mit „Zum Dokument“ zurück.",
      },
      {
        title: "Interne Entscheidung wählen",
        text: "Geben Sie die Auftragsbestätigung frei, fordern Sie eine Rückfrage an oder geben Sie sie nicht frei.",
      },
      {
        title: "Entscheidung dokumentieren",
        text: "Begründen Sie Rückfragen und Nicht-Freigaben. Bei einer Freigabe mit Warnungen bestätigen Sie deren Prüfung ausdrücklich. Unter der Entscheidung sehen Sie den Prüfverlauf und filtern ihn nach Status, Person und Zeitraum oder gruppieren ihn nach Monat.",
      },
    ],
    terms: [
      {
        term: "Über Referenz",
        explanation:
          "Der bestätigte Preis liegt über der angezeigten historischen Vergleichsbasis.",
      },
      {
        term: "Ohne Referenz",
        explanation:
          "Für diese Position ist noch keine geeignete frühere Preisbasis vorhanden.",
      },
      {
        term: "Mehrkosten und Ersparnis",
        explanation:
          "Mehrkosten sind Preise über der Referenz, Ersparnis Preise darunter, jeweils mit der Menge bewertet. Mehrkosten stehen rot mit Plus, Ersparnis grün mit Minus; „Summe Abweichungen“ verrechnet beide.",
      },
      {
        term: "Möglich zum Bestpreis",
        explanation:
          "Betrag, der gegenüber dem niedrigsten angezeigten Vergleichspreis mehr bezahlt würde. Er zeigt das Sparpotenzial, keine zugesagte Kondition.",
      },
    ],
    saving:
      "Gespeichert werden die interne Entscheidung, der Kommentar, der Prüfende, der Zeitpunkt und die dabei sichtbare Prüfgrundlage. Frühere Entscheidungen bleiben im Verlauf erhalten.",
    boundary:
      "Die Freigabe gilt nur innerhalb von TruuCost. Beim Großhändler wird dadurch keine Auftragsbestätigung angenommen, geändert oder abgelehnt.",
    links: [
      {
        label: "Belege öffnen",
        to: "/belege?type=einzelauftrag",
      },
      { label: "Analyse öffnen", to: "/reports/variance" },
    ],
  },
  {
    id: "price-analysis",
    title: "Analyse: Mehrkosten und Einkaufsvolumen",
    listLabel: "Analyse",
    audience: "all",
    useCaseIds: ["UC-PA-001", "UC-PA-002"],
    purpose:
      "Die Analyse beantwortet, wo ihr mehr bezahlt als früher und was ihr wann eingekauft habt. Die Filter oben – Zeitraum, Abweichung, Großhändler, Hersteller – gelten für alle drei Reiter; der Satz darunter sagt in Worten, was gerade gezeigt wird.",
    steps: [
      {
        title: "Mehrkosten Top 10",
        text: "Oben stehen die Mehrkosten nach Großhändler und Hersteller; ein Klick auf einen Eintrag filtert die ganze Seite darauf. Darunter die zehn Artikel mit den höchsten Mehrkosten, je mit der höchsten Abweichung eines Kaufs. Mit „Sortiert nach“ wechselst du zwischen Mehrkosten in € und Abweichung in %.",
      },
      {
        title: "Alle Mehrkosten",
        text: "Der Reiter zeigt jede einzelne teurere Rechnungsposition. Ansicht, „Mehrkosten ab“ und Gruppierung stehen über der Liste; „Ausreichende Vergleichsbasis“ und „Zu wenig Vergleichsdaten“ trennen belastbare von unsicheren Vergleichen. „Export für Excel“ lädt die aktuelle Liste. „Beleg →“ öffnet die Preisprüfung genau an dieser Position.",
      },
      {
        title: "Einkaufsvolumen",
        text: "Hier siehst du die Ausgaben je Monat mit Schnitt, stärkstem und schwächstem Monat, die Anteile nach Großhändler und Hersteller und „Monat für Monat“ mit Positionen, Belegen und Mehrkosten. Der laufende Monat zählt erst, wenn er abgeschlossen ist. „Wann kaufen wir welchen Hersteller?“ zeigt über die letzten drei vollen Jahre, in welchen Monaten ein Hersteller gekauft wird und ob das jedes Jahr so ist.",
      },
      {
        title: "Eigene Filterwerte eingeben",
        text: "Wähle bei Abweichung oder Mehrkosten „Eigener Wert …“. Bei historischen Preisspitzen kannst du auch eine eigene Mindestanzahl früherer Positionen festlegen. Das Zahlenfeld ist sofort bereit zur Eingabe. Mit Enter oder „Übernehmen“ wendest du den Wert an; Escape oder „Abbrechen“ schließt die Eingabe ohne Änderung und bringt dich zum Filter zurück.",
      },
      {
        title: "Prüfregel",
        text: "Ab welcher Abweichung die Rechnungsprüfung eine Position meldet, legt die Prüfregel fest. Du findest sie unter „So wird gerechnet“; geändert wird sie unter Konto › Einstellungen. Die Analyse zeigt beim Öffnen alle Mehrkosten, auch unterhalb der Prüfregel.",
      },
    ],
    terms: [
      {
        term: "Vergleichsbasis",
        explanation: "Frühere, zum Belegdatum bereits bekannte Preise desselben Artikels.",
      },
      {
        term: "Abweichung",
        explanation: "Prozentualer Abstand des Netto-Preises zur Vergleichsbasis.",
      },
      {
        term: "Mehrkosten",
        explanation: "Preisunterschied über der Vergleichsbasis, multipliziert mit der Menge.",
      },
    ],
    saving:
      "Ein Preisbericht speichert keine Entscheidung. Erklärung festhalten schließt die gewählte Prüfung ab; Rückfrage dokumentieren setzt sie in Bearbeitung. Eine gespeicherte frühere Entscheidung bleibt sichtbar. Hat sich ihr dokumentierter Vergleichspreis geändert, werden damaliger und aktueller Wert gegenübergestellt. Verlauf und weitere Schritte bleiben im Beleg erreichbar.",
    boundary:
      "TruuCost entscheidet nicht automatisch, dass ein Preis falsch ist. Spätere Belege desselben Artikels werden nicht still mit erledigt.",
    links: [
      { label: "Analyse öffnen", to: "/reports/variance" },
      { label: "Offene Belegprüfungen", to: "/rechnungspruefung?review=open" },
    ],
  },
  {
    id: "article",
    title: "Artikel finden und bestellen",
    listLabel: "Artikel",
    audience: "all",
    useCaseIds: ["UC-EK-003"],
    purpose:
      "Artikel suchen, den letzten und besten Preis sehen und in den Warenkorb legen.",
    steps: [
      {
        title: "Artikel suchen",
        text: "Unter „Artikel“ suchst du nach Nummer, Bezeichnung oder Maß; jedes Wort muss vorkommen („kugelhahn 3/4“, „DN 20“). Darunter grenzen Hersteller, Großhändler (bei wem gekauft), Kostenstelle und „Gekauft: letzte 3, 6 oder 12 Monate“ ein – ein Klick wählt, ein zweiter hebt auf, mehrere sind möglich. „Alle … ▾“ öffnet die vollständige Liste mit Suche. „Zuletzt gekauft“ oder „Häufig gekauft“ legt die Reihenfolge fest; „Käufe“ zählt die Rechnungen im gewählten Zeitraum, ohne Zeitraum der letzten 12 Monate.",
      },
      {
        title: "Preise einordnen",
        text: "Die Artikelseite zeigt oben Bezeichnung, Artikelnummer, Hersteller und Großhändler. Darunter stehen letzter Einkauf, niedrigster Preis und Durchschnitt im gewählten Zeitraum, der Preisverlauf und die Belege dazu; dort ist der niedrigste Preis grün und der höchste rot markiert. Bei jedem Beleg zeigt „Abweichung“, wie weit der Preis über (rot) oder unter (grün) dem bisherigen Vergleichspreis desselben Artikels lag – derselbe Wert wie in den Belegdetails. Kommt ein Artikel von mehreren Großhändlern, erscheinen die Preise je Großhändler.",
      },
      {
        title: "In den Warenkorb",
        text: "„In den Warenkorb“ legt den Artikel in deinen aktiven Warenkorb – den du im Warenkorb zuletzt ausgewählt hast, sonst den zuletzt bearbeiteten. Danach zeigt die Zeile „− Menge +“: Plus und Minus, Eintippen oder die Pfeiltasten ändern die Menge, das Warenkorb-Symbol führt hinein. Die Zahl am Warenkorb oben rechts zeigt, wie viele Positionen darin liegen. Im Warenkorb änderst du Mengen genauso, den Namen über den Stift; „×“ am Warenkorb löscht ihn nach einer Rückfrage. Im Warenkorb wählst du oben, aus welchem Zeitraum der Bestpreis stammt (6 oder 12 Monate, 2 oder 3 Jahre); Preis und Vorgabe bleiben bei den letzten 12 Monaten.",
      },
    ],
    terms: [
      {
        term: "Frühere Preisanfrage",
        explanation: "Eine im Browser gespeicherte Preisanfrage wird beim Öffnen des Warenkorbs einmal übernommen. Bei einer Unterbrechung kannst du „Übernahme erneut versuchen“ wählen oder neu laden. Bereits übernommene Positionen werden dabei nicht verdoppelt. Die Browser-Vorlage bleibt erhalten. Meldet TruuCost eine andere oder eine ältere Übernahme, prüfe „Alle Warenkörbe“ und ergänze fehlende Positionen dort.",
      },
      {
        term: "Käufe",
        explanation: "Anzahl der Rechnungen mit diesem Artikel im gewählten Zeitraum; Gutschriften zählen nicht.",
      },
    ],
    saving:
      "Die Artikelseite speichert nichts. Der gewählte Zeitraum wird für dich gemerkt.",
    boundary:
      "Ein früherer niedriger Preis ist keine aktuelle Preiszusage; Mengen, Großhändler und Konditionen können sich unterscheiden.",
    links: [
      { label: "Artikel öffnen", to: "/articles" },
      { label: "Warenkorb öffnen", to: "/warenkorb" },
    ],
  },
  {
    id: "offers",
    title: "Angebote im Preisspiegel vergleichen",
    listLabel: "Preisspiegel",
    audience: "all",
    useCaseIds: ["UC-EK-002"],
    purpose:
      "Zwei oder drei Angebote Position für Position nebeneinander vergleichen und die besten Preise übernehmen.",
    steps: [
      {
        title: "Angebote einlesen",
        text: "Unter Bestellungen › Angebote oder Preisspiegel öffnest du „PDF-Angebote direkt prüfen“. Wähle ein bis drei digitale SHT-, GC/Odörfer-, Pichler- oder Danfoss-Originalangebote aus. Ein Angebot wird mit früheren Einkäufen verglichen, mehrere Angebote untereinander. Diese Prüfung speichert die PDFs und Ergebnisse nicht dauerhaft; beim Schließen, Verlassen oder Neuladen endet sie. Gespeicherte Angebote stehen weiterhin in der Liste darunter. Pichler wird als Hersteller/Direktlieferant ausgewiesen. Danfoss bleibt als Hersteller gekennzeichnet; Vertriebsweg und unbekannter Verkäufer stehen getrennt dabei. Danfoss-Angebote prüfst du derzeit einzeln am PDF. Auch bei gleichen Artikelnummern und Bedingungen werden keine Preisrangfolgen zwischen Danfoss-Angeboten gebildet. Einkaufshistorie, Einkaufsauswahl und Preisrückfragen bleiben dann offen. Über „Positionsart“ trennst du Waren, Dienstleistungen, Informationszeilen und offene Arten; bei Pichler bezeichnet die Zeilennummer die Reihenfolge im PDF, bei Danfoss wird die gedruckte Positionsnummer angezeigt.",
      },
      {
        title: "Im Preisspiegel auswählen",
        text: "Öffne „Preisspiegel“, hake zwei oder drei Angebote an und wähle „Angebote vergleichen“. „Angebotsauswahl ändern“ führt zurück zur Auswahl. Kann ein Angebot nicht geladen werden, bringt dich „Zur Angebotsauswahl“ mit derselben Auswahl und denselben Listenfiltern zurück. Das betrachtete Angebot wird dabei nicht hinzugefügt.",
      },
      {
        title: "Preise vergleichen",
        text: "Der niedrigste vergleichbare Preis ist dezent grün markiert. Die gemeinsame Summe umfasst nur eindeutig gleiche Artikel mit gleichen Mengen, Einheiten und Währungen; offene Positionen bleiben sichtbar. Mit „Einkauf zusammenstellen“ wählst du die günstigsten Preise.",
      },
      {
        title: "Rückfrage vorbereiten",
        text: "In der Einzelansicht eines Angebots bereitest du eine Rückfrage zu Positionen über deinem Zielpreis vor. Den Text kopierst oder lädst du herunter; TruuCost versendet nichts.",
      },
    ],
    terms: [
      {
        term: "Preisspiegel",
        explanation: "Gegenüberstellung mehrerer Angebote je Position.",
      },
    ],
    saving:
      "Der Vergleich speichert keine Bestellung. Bereits gespeicherte Angebote bleiben unverändert. Alternativen und Optionalartikel stehen mit eigener Kennzeichnung im Ergebnis und im Filter „Alternativen & Optionalartikel“. Sie sind einzeln prüfbar, zählen aber nicht zu regulären Summen, gemeinsamer Einkaufsauswahl, Zielabweichung oder Preisrückfrage. Fehlende Paketpreise bleiben offen. Pichler-Dienstleistungen und Sammel-/Sonderartikelcodes erhalten keine automatische Preisbewertung oder Einkaufsauswahl. Gedruckte Rabatte bleiben zusammen mit Druckpreis und Preis nach Rabatt sichtbar. Bei Preisen je 100 oder anderen ausdrücklich gedruckten Preisbasen stehen Druckwert und Preis je Einheit getrennt; ohne belegte Preisbasis gibt es keinen Vergleich. Prüfhinweise und Originalseiten bleiben erreichbar. Bei Danfoss zeigen die Originalangaben auch die belegten Zwischen- und Endsummen, Variantenbezüge und Bedingungen. Gedruckte Rechenabweichungen bleiben unverändert und sperren den Vergleich der betroffenen Position.",
    boundary:
      "Gute Vergleichspreise bestätigen nicht Lieferumfang oder Konditionen; das prüfst du am Original.",
    links: [
      { label: "Preisspiegel öffnen", to: "/preisspiegel" },
      { label: "Angebote öffnen", to: "/angebote" },
    ],
  },
  {
    id: "cost-centers",
    title: "Ausgaben über Kostenstellen verstehen",
    listLabel: "Kostenstellen",
    audience: "all",
    useCaseIds: ["UC-CC-001", "UC-CC-002", "UC-CC-005"],
    purpose:
      "Kostenstellen ordnen Ausgaben nach Projekten, Lieferadressen und Kundenreferenzen, ohne die ursprünglichen Belegdaten zu verändern.",
    steps: [
      {
        title: "Passende Sicht wählen",
        text: "Wechseln Sie zwischen Projekten, Adressen und Kunden und wählen Sie den gewünschten Zeitraum. Suche, Kostenstellentyp, Zeitraum und die Darstellung der Kundenreferenzen werden für Sie und Ihren Betrieb gemerkt. Die Auswahl bleibt beim Typwechsel und beim Rückweg aus Details erhalten. Beim Öffnen einer Kostenstelle gilt der gewählte Ausgabenzeitraum auch für Summe, Verlauf und zugehörige Positionen. Im Detail kannst du den Zeitraum ändern oder den gesamten Zeitraum ansehen. Die zugehörigen Positionen zeigen Datum, Beleg, Artikel, Kostenstelle und Betrag; weitere Positionen erreichst du über die Seitennavigation. Suche und Sortierung gelten für alle Positionen im Zeitraum. Gruppieren Sie nach Großhändler, Beleg oder Monat; dafür werden alle passenden Positionen geladen. Der Excel-Export enthält alle Suchtreffer, bei Gruppierung ohne eingeklappte Gruppen. Gruppenüberschriften und Summen können Sie im Export ergänzen. Auf dem Handy stehen die Angaben untereinander. Ein direkter Detaillink ohne Zeitraum zeigt die Gesamtsicht. Quellen und manuelle Zuordnungen bleiben unter Details unabhängig vom Ausgabenzeitraum erreichbar. Der Hinweis „Details“ öffnet diesen Reiter direkt. „Bearbeiten“ oben an der Kostenstelle öffnet die gemeinsame Bearbeitung. Bei möglichen Zuordnungen öffnet auch der Link „Bearbeiten“ im Hinweis direkt diesen Dialog. Konkrete Links haben Vorrang. „Filter zurücksetzen“ zeigt alle Kostenstellen, die letzten zwölf Monate und gruppierte Kundenreferenzen.",
      },
      {
        title: "Ausgaben öffnen",
        text: "Öffnen Sie eine Kostenstelle, um zugehörige Belege und Ausgaben zu sehen. Mit „Rechnungspreise prüfen“ öffnen Sie den Preisbericht für diese Kostenstelle im angezeigten Zeitraum. Gutschriften sind dort nicht enthalten; frühere Einkäufe bleiben die Vergleichsbasis. Zeitraum und Schwellen können Sie für diesen Bericht ändern. „Zurück zur Kostenstelle“ führt zur Ausgangsansicht zurück.",
      },
      {
        title: "Bezeichnungen ergänzen",
        text: "Geben Sie schwer lesbaren Quellenangaben bei Bedarf einen verständlichen Anzeigenamen.",
      },
      {
        title: "Zusammengehöriges gruppieren",
        text: "Ordnen Sie bestehende Kostenstellen einem Projekt oder Sammelprojekt zu, wenn Sie diese gemeinsam auswerten möchten.",
      },
    ],
    terms: [
      {
        term: "Projekt",
        explanation: "Eine Kostenstelle für ein einzelnes Vorhaben.",
      },
      {
        term: "Sammelprojekt",
        explanation: "Eine manuelle Gruppierung mehrerer bestehender Kostenstellen.",
      },
      {
        term: "Quelle",
        explanation: "Die ursprüngliche Angabe aus PDF, UGL, DATANORM oder einer bewussten Benutzereingabe.",
      },
    ],
    saving:
      "Anzeigenamen, Gruppierungen und manuelle Zuordnungen werden zusätzlich zur Quelle gespeichert und bleiben nachvollziehbar.",
    boundary:
      "Eine manuelle Zuordnung überschreibt keine Quellenangabe und zählt denselben Beleg nicht doppelt zu den Gesamtausgaben.",
    links: [
      { label: "Kostenstellen öffnen", to: "/cost-centers" },
      { label: "Projekte anzeigen", to: "/cost-centers?type=project" },
    ],
  },
  {
    id: "imports-sources",
    title: "Importe und Quellen prüfen",
    listLabel: "Import & Quellen",
    audience: "all",
    useCaseIds: ["UC-IM-001", "UC-IM-002", "UC-DB-001"],
    purpose:
      "Import & Quellen zeigt, welche Dateien übernommen wurden, wie sie erkannt wurden, welche Qualitätsfragen offen sind und welche Nachweise für Auswertungen vorhanden sind.",
    steps: [
      {
        title: "Mehrere Dateien hochladen",
        text: "Wählen Sie mehrere Dateien oder ZIP-Archive aus oder ziehen Sie Dateien und ganze Ordner in den Uploadbereich. Unterordner werden mit eingelesen. Verschiedene Belegarten und Großhändler dürfen gemeinsam hochgeladen werden. Jede Datei wird einzeln automatisch erkannt. Sie können auch während der Verarbeitung weitere Dateien hinzufügen. Erledigte Dateien verschwinden aus der Warteschlange und erscheinen als Ergebnis. Bereits vorhandene Belege werden übersprungen und mit ihrer ursprünglichen Datei verknüpft. Noch nicht gestartete Dateien können entfernt werden. Nur bei einer einzelnen Datei bietet „Erkennung korrigieren“ bei Bedarf eine manuelle Zuordnung. Erlaubt sind bis zu 200 Dateien und 200 MiB insgesamt, auch entpackt, sowie 50 MiB je Datei. Jede Datei erhält ein eigenes Ergebnis. Beim Abbrechen bleiben bereits übernommene Dateien erhalten; die laufende Datei kann noch abgeschlossen werden. Prüfen Sie deren Status im Importverlauf.",
      },
      {
        title: "Importstatus prüfen",
        text: "Oben bei den Importen sehen Sie, wann zuletzt Dateien zum Bestand hinzugefügt wurden. Öffnen Sie „Importdetails nach Format und Großhändler“ für die Dateianzahl, vorhandene Belege und den letzten Upload mit Benutzer und Status. Aufnahmezeitpunkt und Belegdatum sind unterschiedliche Angaben; unbekannte Zeitpunkte bleiben als solche sichtbar. Laufende Importe öffnen Sie über die angezeigte Anzahl. Jeder Import, der auf Sie wartet, steht einzeln unter „Braucht deine Aufmerksamkeit“, mit Grund und „Öffnen“, egal wie alt er ist. Ein Upload kann noch auf Prüfung warten und gehört dann noch nicht zum Dateibestand. Steht dort „PDF fehlt – Original ergänzen“, wählen Sie die ursprüngliche Datei; sie wird sofort eingelesen und geprüft. Nach „Übernehmen“ oder „Ablehnen“ bestätigt die Prüfung, dass der Fall erledigt ist, und bietet den nächsten offenen Fall an.",
      },
      {
        title: "Erkennung und Datenquellen kontrollieren",
        text: "In der Import-Prüfung sehen Sie das Original-PDF neben den erkannten Belegdaten und Positionen. Vergleichen Sie Mengen und Nettopreise über die Seitenverweise mit dem Original. Ein Strich kennzeichnet nicht erkannte Werte. Wählen Sie bei unklarer Erkennung Belegart und Großhändler direkt neben dem PDF und klicken Sie auf „Erneut prüfen“. Kontrollieren Sie die neu eingelesenen Positionen und bestätigen Sie anschließend mit „Übernehmen“. Fehlen Positionen, bleibt die Übernahme gesperrt und das Original zur Prüfung gespeichert. Über den Seitenverweis gelangen Sie zur betroffenen Stelle. Eine abweichende Belegsumme muss vor der Übernahme mit dem Original verglichen werden. Eine Ablehnung lässt sich mit „Ablehnung rückgängig machen“ wieder öffnen. Wenn ein älteres Original bereits gelöscht wurde, können Sie es in derselben Prüfung erneut auswählen. Unter „Einstellungen“ → „Datenquellen“ sehen Sie je Großhändler, welche Quellen erwartet werden und welche Qualitätsprüfungen aktiv, wartend oder nicht anwendbar sind.",
      },
      {
        title: "Hilti-Warenrechnungen prüfen",
        text: "Hilti-PDFs bleiben nach dem Hochladen zur manuellen Prüfung offen. Kontrollieren Sie Preis je 1 oder 100 Einheiten, Positionsrabatte, Sondernettopreise und gedruckte Nettobeträge neben dem Original. Der abgeleitete Nettoeinzelpreis dient dem Preisvergleich; Skonto und Zuschläge werden nicht auf Artikelpreise verteilt. Mit „Übernehmen“ geben Sie den Beleg frei. Die Übernahme läuft anschließend weiter; Sie können die Prüfung schließen und den Stand im Importverlauf öffnen. Erst wenn Beleg und Auswertungen fertig sind, erscheint „Übernommen“ mit dem Link zum Beleg und zum nächsten offenen Fall. Wird die Verarbeitung unterbrochen, prüfen Sie zuerst den gespeicherten Status und laden die Datei nicht vorsorglich erneut hoch. Bei fehlenden oder widersprüchlichen Angaben bleibt die Übernahme gesperrt. Reparaturrechnungen werden separat behandelt. Im übernommenen Beleg öffnet „Hilti: Preisbasis und Rabatte im Original“ die freigegebenen Druckwerte und PDF-Seiten. Bestellreferenz und Lieferadresse werden getrennt mit PDF-Quelle angezeigt. Lagerangaben liefern Lagerkontext; eindeutige AU-Nummern und ältere achtstellige Nummern werden als eigene Auftrags-/Kostenstellenreferenzen erhalten. Ein Projekt kann mehrere Aufträge haben. Freie Angaben und widersprüchliche Referenzen bleiben zur manuellen Prüfung offen; die Firmenadresse allein bestätigt kein Projekt und führt keine Aufträge zusammen. Über „Zuordnung offen“ bei einer Hilti-Position wählen Sie ein vorhandenes Projekt, einen Auftrag oder eine Bestellreferenz als Kostenstelle; reine Lieferadressen bleiben Liefernachweise. Sie prüfen das Original-PDF und begründen die Entscheidung. Sie können eine Position auch begründet zur Klärung offen lassen. Eine manuelle Zuordnung ist an der Position erkennbar, kann geändert oder zurückgenommen werden und bleibt mit Person, Zeitpunkt und PDF-Nachweis in der Historie erhalten. Die Originalangaben bleiben unverändert. Eine weitere PDF mit derselben Rechnungsnummer ersetzt keinen vorhandenen Beleg automatisch.",
      },
      {
        title: "Nachweise öffnen",
        text: "Unter „Dateien“ finden und filtern Sie Ihre Unterlagen. Suche, Filter und Sortierung werden für Sie und Ihren Betrieb gemerkt. Aktive Zusatzfilter sind beim Einstieg geöffnet. „Filter zurücksetzen“ zeigt wieder alle Dateien, zuletzt importierte zuerst. Die Auswahl bleibt beim Öffnen und Schließen einer Datei erhalten; konkrete Dateilinks haben Vorrang. Beim Öffnen sehen Sie das Original, Importdetails und unter „Weitere Aktionen“ die Möglichkeit, eine Datei aus Auswertungen auszunehmen. „Datenabdeckung ansehen“ zeigt den Gesamtstand über alle Großhändler.",
      },
      {
        title: "Ganzen Beleg erneut prüfen",
        text: "Bei unvollständig zugeordneten Positionen öffnen Sie in der Rechnungsprüfung die Aktion „Ganzen Beleg erneut prüfen“. Vergleichen Sie die bisherige und neue Auswertung mit der Positionssumme im Original. Fehlende Positionen und manuelle Berichtigungen werden angezeigt. Die Vorschau verändert keine gespeicherten Angaben. Wählen Sie für neue Positionen den passenden Artikel anhand des Originals. Ohne passenden Vorschlag können Sie über „Artikel suchen“ nach Nummer, Bezeichnung oder Hersteller im vorhandenen Bestand suchen. „Auswahl ändern“ öffnet die Suche wieder. Erst „Auswahl prüfen“ aktualisiert die Vorschau; die Auswahl speichert noch nichts. Ohne Treffer können Sie den Beleg später weiterprüfen. Wenn keine Konflikte mehr offen sind, bestätigen Sie den Originalvergleich und wählen „Beleg übernehmen“. Bestehende Zuordnungen bleiben erhalten. Nach einem abgeschlossenen Rechnungs- oder Gutschriftimport führt „Beleg öffnen“ zu den gespeicherten Positionen und Beträgen. „Zurück zum Import“ bringt Sie wieder zur Importansicht. Bei bereits vorhandenen Dateien öffnet „Vorhandenen Beleg öffnen“ den bestehenden Beleg. Laufende Importansichten aktualisieren sich automatisch. Auf der Belegseite können Sie unter „Änderungsverlauf“ die Übernahme rückgängig machen. Die Vorschau zeigt die frühere Summe und entfernte Positionen. Neuere Änderungen oder Prüfentscheidungen verhindern das Zurücksetzen.",
      },
      {
        title: "Eingelesene Daten prüfen",
        text: "Unter „Daten prüfen“ auf der Importseite finden berechtigte Nutzer offene Artikelzuordnungen aus Rechnungen, Gutschriften, Angeboten und Aufträgen. Die Dokumentart steht an jeder Position. Angebote und Auftragsbestätigungen verlinken direkt auf ihre Zuordnungen; „Zum Dokument“ führt zurück. „Zuordnungen klären“ öffnet die Liste; jede Position führt direkt zum Original und zur Entscheidung. Bewusst ohne Zuordnung belassene Positionen gelten als bearbeitet. Eine leere offene Liste bietet „Alle Zuordnungen ansehen“. Rückgängig gemachte Entscheidungen öffnen den ursprünglichen Prüffall wieder. Fehlende Vergleichspreise allein erzeugen keine Zuordnungsaufgabe. Daneben öffnen Sie Hinweise zu Preisangaben, Belegabgleichen und Artikelsets. Preisangaben sind nach Beleg gebündelt. „Beleg prüfen“ zeigt offene, zur Klärung markierte und erledigte Aufgaben; „Nächste offene Position“ führt zum Originalvergleich. Nach dem Speichern gelangen Sie zum aktualisierten Aufgabenstand zurück. „Später weiterprüfen“ führt zur Belegliste; offene Aufgaben bleiben dort auffindbar. Erledigte Prüfaufgaben bedeuten keine Freigabe der Lieferantenrechnung. Die Anzahl umfasst alle aktuellen Auffälligkeiten; in der Liste unterscheiden Sie offene und bereits bearbeitete Hinweise. Eine leere offene Prüfliste bietet „Alle Status anzeigen“; bei nicht aktiven Prüfungen führt „Verlauf“ zu früheren Entscheidungen. Unter „Weitere Prüfungen“ sehen Sie, welche Abgleiche zusätzliche Dateien benötigen. Dokumentieren Sie in der Arbeitsliste, ob ein Hinweis plausibel ist oder eine Korrektur braucht.",
      },
      {
        title: "Verantwortung beachten",
        text: "Alle Rollenbereiche dürfen Nachweise und Qualitätsprüfungen ansehen. Belege hochladen dürfen Einkauf und Controlling. Neue Dateien übernehmen und unsichere Importe bestätigen liegt beim Controlling; Quellenprofile anpassen und Qualitätsentscheidungen speichern erfordern den Rollenbereich Administrator. Die lokale Identitätsdiagnose bleibt im neuen Rollenmodell bewusst nicht verfügbar.",
      },
    ],
    terms: [
      {
        term: "Prüfung erforderlich",
        explanation: "Großhändler oder Belegtyp konnte nicht sicher genug erkannt werden.",
      },
      {
        term: "Abgeschlossen",
        explanation: "Die bestätigten Daten wurden übernommen und die Ansichten aktualisiert.",
      },
      {
        term: "Datenabdeckung",
        explanation: "Zeigt je Bereich, wie viele Einheiten vollständig belegt sind, fachlich geprüft werden müssen oder fehlende Nachweise haben. Details erklären fehlende Nachweise und Grenzen der vorhandenen Quelldaten.",
      },
      {
        term: "Daten prüfen",
        explanation: "Zeigt offene Hinweise zu übernommenen Angaben. Bei Rechnungspositionen vergleichst du das Original direkt mit Menge, Einzelpreis und Positionssumme. Lange Artikelbezeichnungen kannst du vollständig aufklappen. Wähle nach dem Originalvergleich einen passenden Grund oder schreibe eine eigene Begründung. Ein ausgewählter Grund wird mit deiner Entscheidung gespeichert; eine Ergänzung ist optional. Zum Wiederöffnen ist weiterhin eine eigene Begründung nötig. Du kannst eine erklärte Abweichung mit Begründung abschließen oder Angaben zur Klärung markieren. Über „Angabe berichtigen“ kannst du Menge, Netto-Einzelpreis je angezeigter Einheit und Positionssumme aus dem Original übernehmen. Leere Mengen- oder Preisfelder bleiben unverändert; die Positionssumme wird nicht automatisch berechnet. Öffne „Auswirkung ansehen“ und vergleiche alte und neue Angaben sowie die neue Belegsumme. Bestätige erst danach mit „Korrektur übernehmen“. Bei unvollständig zugeordneten Positionen muss zuerst der ganze Beleg geprüft werden. Unter „Berichtigte Angaben“ findest du den Änderungsverlauf und kannst eine Berichtigung nach einer erneuten Vorschau rückgängig machen. Über „Später prüfen“ bleibt ein Hinweis offen.",
      },
      {
        term: "Quellenprofil",
        explanation: "Legt je Großhändler fest, ob PDF, UGL und DATANORM regelmäßig erwartet, nur falls vorhanden genutzt oder nicht geliefert werden.",
      },
      {
        term: "Datenquellen",
        explanation: "Bestimmen im eigenen Bereich je Großhändler, welche Qualitätsprüfungen anhand der erwarteten Quellen anwendbar sind.",
      },
    ],
    saving:
      "Beim Bestätigen werden nur die geprüften Importdaten übernommen. Qualitätsentscheidungen werden mit Begründung nachvollziehbar gespeichert; die ursprüngliche Datei bleibt unverändert als Nachweis erhalten.",
    boundary:
      "TruuCost meldet sich nicht selbst beim Großhändler an und lädt keine Lieferantendateien automatisch herunter.",
    links: [
      { label: "Import öffnen", to: "/imports" },
      { label: "Qualitätsprüfung öffnen", to: "/imports/review" },
      { label: "Quellen prüfen", to: "/coverage" },
      { label: "Datenquellen öffnen", to: "/coverage?tab=profiles" },
    ],
  },
  {
    id: "members",
    title: "Benutzer und Rollen verwalten",
    listLabel: "Benutzerverwaltung",
    audience: "admin",
    useCaseIds: ["UC-AD-001"],
    purpose:
      "Über das Konto-Menü oben rechts öffnest du „Benutzerverwaltung“. Hier verwalten zuständige Administratoren, wer auf den Mandanten zugreifen darf und welche festen Rollenbereiche eine Person erhält. Im bisherigen Rollenmodell bleiben Mitglied und Admin unverändert.",
    steps: [
      {
        title: "Zugriff für ein bestehendes Konto vergeben",
        text: "Geben Sie die E-Mail-Adresse eines bestehenden TruuCost-Kontos ein. Wählen Sie im bisherigen Modell Mitglied oder Admin, im neuen Modell einen oder mehrere feste Rollenbereiche.",
      },
      {
        title: "Rolle eines Mitglieds ändern",
        text: "Die Rolle einer bereits zugeordneten Person ändern Sie direkt in der Liste der Benutzer mit Zugriff.",
      },
      {
        title: "Rollenbereiche kombinieren",
        text: "Controlling und Einkauf lassen sich kombinieren. Controlling umfasst Belege, Preisprüfung, Gutschriften, Kostenstellen und das Hochladen von Belegen. Administrator ist ein zusätzlicher Bereich und ersetzt keine Fachrolle. Nur lesen steht immer allein.",
      },
      {
        title: "Mindestens einen Admin behalten",
        text: "Es muss mindestens einen Admin geben. Ernennen Sie bei Bedarf zuerst ein weiteres Mitglied zum Admin.",
      },
    ],
    terms: [
      {
        term: "Nur lesen",
        explanation:
          "Darf freigegebene Mandantendaten ansehen, aber keine fachlichen Entscheidungen oder Verwaltungsänderungen speichern.",
      },
      {
        term: "Einkauf",
        explanation:
          "Legt Warenkörbe an, bestellt, prüft Auftragsbestätigungen und lädt Belege hoch. Die Freigabe liegt beim Controlling.",
      },
      {
        term: "Controlling",
        explanation:
          "Prüft Belege und Preise, ordnet Gutschriften zu, verwaltet Kostenstellen und lädt Belege hoch.",
      },
      {
        term: "Administrator",
        explanation:
          "Verwaltet Benutzer, Importe, Quellen und Datenqualität. Der Bereich enthält keine Fachrolle automatisch.",
      },
      {
        term: "Mitglied und Admin",
        explanation:
          "Sind die beiden Bezeichnungen im bisherigen Rollenmodell. Deren bestehende Berechtigungen bleiben unverändert.",
      },
      {
        term: "Mandant",
        explanation:
          "Der getrennte Unternehmensbereich, für den der Zugriff erteilt wird.",
      },
    ],
    saving:
      "Das Vergeben oder Entfernen eines Zugriffs und Rollenänderungen werden jeweils gespeichert und nachvollziehbar protokolliert.",
    boundary:
      "Hier wird nur ein bereits registriertes Konto zugeordnet. Neue Konten und Einladungen sind noch nicht möglich.",
    links: [{ label: "Benutzerverwaltung öffnen", to: "/members" }],
  },
] as const;

export const HELP_ROUTE_MAPPINGS: readonly HelpRouteMapping[] = [
  { kind: "exact", path: "/", guideId: "overview" },
  { kind: "exact", path: "/belege", guideId: "belege" },
  { kind: "children", path: "/belege", guideId: "belege" },
  {
    kind: "exact",
    path: "/auftragsbestaetigungen",
    guideId: "order-confirmations",
  },
  {
    kind: "children",
    path: "/auftragsbestaetigungen",
    guideId: "order-confirmations",
  },
  { kind: "exact", path: "/reports/variance", guideId: "price-analysis" },
  { kind: "exact", path: "/articles", guideId: "article" },
  { kind: "children", path: "/articles", guideId: "article" },
  { kind: "exact", path: "/preisanfrage", guideId: "order-confirmations" },
  { kind: "exact", path: "/angebote", guideId: "offers" },
  { kind: "children", path: "/angebote", guideId: "offers" },
  { kind: "exact", path: "/orders", guideId: "article" },
  { kind: "children", path: "/orders", guideId: "article" },
  { kind: "exact", path: "/rechnungspruefung", guideId: "belege" },
  { kind: "exact", path: "/preisspiegel", guideId: "offers" },
  { kind: "exact", path: "/bestellungen", guideId: "order-confirmations" },
  { kind: "exact", path: "/warenkorb", guideId: "article" },
  { kind: "exact", path: "/einstellungen", guideId: "price-analysis" },
  { kind: "exact", path: "/cost-centers", guideId: "cost-centers" },
  { kind: "children", path: "/cost-centers", guideId: "cost-centers" },
  { kind: "exact", path: "/projects", guideId: "cost-centers" },
  { kind: "children", path: "/projects", guideId: "cost-centers" },
  { kind: "exact", path: "/imports", guideId: "imports-sources" },
  { kind: "exact", path: "/imports/review", guideId: "imports-sources" },
  { kind: "exact", path: "/coverage", guideId: "imports-sources" },
  { kind: "exact", path: "/members", guideId: "members" },
] as const;

export function getHelpGuide(id: string | null): HelpGuide | null {
  if (!id) return null;
  return HELP_GUIDES.find((guide) => guide.id === id) ?? null;
}

/** Under one address (#546) the document's own kind picks the guide. */
const BELEG_KIND_GUIDES: Record<string, string> = {
  "angebot": "offers",
  "einzelauftrag": "order-confirmations",
};

export function getHelpGuideForPath(pathname: string, search = ""): HelpGuide {
  // Gutschriften sind eine Belegart der Belegliste (#539) und behalten ihre
  // eigene Anleitung, weil ihre Nachweise eigene Regeln haben.
  if ((pathname === "/belege" || pathname === "/rechnungspruefung") && new URLSearchParams(search).get("type") === "gutschrift") {
    const credits = getHelpGuide("credits");
    if (credits) return credits;
  }
  if (pathname.startsWith("/belege/") && pathname !== "/belege/gutschriften") {
    const kind = decodeURIComponent(pathname.slice("/belege/".length)).split(":")[0];
    const guide = getHelpGuide(BELEG_KIND_GUIDES[kind] ?? null);
    if (guide) return guide;
  }
  const mapping = HELP_ROUTE_MAPPINGS.find((candidate) =>
    routeMappingMatches(candidate, pathname),
  );
  return getHelpGuide(mapping?.guideId ?? "overview") ?? HELP_GUIDES[0];
}

export function getAvailableHelpGuides(admin: boolean): readonly HelpGuide[] {
  return getAvailableHelpGuideGroups(admin).flatMap((group) => group.guides);
}

export function getAvailableHelpGuideGroups(
  admin: boolean,
): readonly AvailableHelpGuideGroup[] {
  return HELP_GUIDE_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    guides: group.guideIds.flatMap((guideId) => {
      const guide = getHelpGuide(guideId);
      return guide && canOpenHelpGuide(guide, admin) ? [guide] : [];
    }),
  })).filter((group) => group.guides.length > 0);
}

export function canOpenHelpGuide(guide: HelpGuide, admin: boolean): boolean {
  return guide.audience === "all" || admin;
}

function routeMappingMatches(
  mapping: HelpRouteMapping,
  pathname: string,
): boolean {
  if (mapping.kind === "exact") return pathname === mapping.path;
  return pathname.startsWith(`${mapping.path}/`);
}
